import { memo, type ChangeEvent, useCallback, useMemo } from 'react';
import type {
  Flow,
  LoadHandlingPoiSpec as LoadHandlingPoiSpecType,
  SingularStationArea,
} from '@thive/fddm-types';
import { useTranslation } from 'react-i18next';

import { GapHorizontalIcon } from '@/assets/icons';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { NumberInput } from '@/modules/common/components/inputs/components/NumberInput';
import { NumUtils } from '@/modules/common/utils';

import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { SnackbarServiceFactory } from '@/modules/snackbar/service/SnackbarServiceFactory';
import { getCommonBoolean, getCommonNumber } from '@/modules/common/helpers/commonValue';
import { MixedSwitch } from '@/modules/common/components/inputs';
import { DisabledReasonTooltip } from '../common/DisabledReasonTooltip';
import {
  createEmptyAreas,
  pushAreaToItsCollection,
} from '@/modules/floorplanDesign/helpers/helpers';

interface LoadHandlingPoiSpecProps {
  selectedSingularStationAreas: SingularStationArea[];
}

type StationWithPoiSpec = {
  area: SingularStationArea;
  poiSpec: LoadHandlingPoiSpecType;
};

/** @description Only intake stations can be used for parking. */
const isIntakeStation = ({ area }: StationWithPoiSpec): boolean =>
  area.loadHandlingType === 'INTAKE';

/** @description A parking priority exists when the station allows parking and has an extension. */
const hasParkingPriority = ({ poiSpec }: StationWithPoiSpec): boolean =>
  Boolean(poiSpec.canBeUsedForParking) && poiSpec.parkingExtension !== undefined;

/** @description Returns the flow with its pre-announce duration reset, or null when there is none. */
const withoutPreAnnounceDuration = (flow: Flow): Flow | null => {
  if (flow.type === 'TRANSPORT') {
    if (flow.transportExtension && flow.transportExtension.preAnnounceDuration > 0) {
      return {
        ...flow,
        transportExtension: {
          ...flow.transportExtension,
          preAnnounceDuration: 0,
        },
      };
    }
  } else if (flow.type === 'CONSOLIDATION_STORAGE') {
    if (
      flow.consolidationStorageExtension &&
      flow.consolidationStorageExtension.preAnnounceDuration &&
      flow.consolidationStorageExtension.preAnnounceDuration > 0
    ) {
      return {
        ...flow,
        consolidationStorageExtension: {
          ...flow.consolidationStorageExtension,
          preAnnounceDuration: 0,
        },
      };
    }
  }

  return null;
};

function LoadHandlingPoiSpecComponent({ selectedSingularStationAreas }: LoadHandlingPoiSpecProps) {
  const mode = useAppStore((state) => state.mode);
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);
  const snackbarSvc = useMemo(
    () => SnackbarServiceFactory.getInstance().createSnackbarService(),
    [],
  );
  const { t } = useTranslation('interface');

  const isDisabled = mode !== 'EDITABLE';

  const stations = useMemo(
    () =>
      selectedSingularStationAreas.flatMap((area): StationWithPoiSpec[] => {
        const [poiSpec] = area.loadHandlingPoiSpecs;
        return poiSpec ? [{ area, poiSpec }] : [];
      }),
    [selectedSingularStationAreas],
  );

  // null means the selected stations have different values ('Mixed').
  // Switches can't show 'Mixed', so a mixed switch shows as off and switching it on sets all.
  const commonValues = useMemo(() => {
    const poiSpecs = stations.map(({ poiSpec }) => poiSpec);

    return {
      deltaXMm: getCommonNumber(poiSpecs.map(({ deltaX }) => NumUtils.metersToMmRounded(deltaX))),
      deltaYMm: getCommonNumber(poiSpecs.map(({ deltaY }) => NumUtils.metersToMmRounded(deltaY))),
    };
  }, [stations]);

  // Allow parking only applies to intake stations: shown when any is, editable when all are
  const parkingStations = useMemo(() => stations.filter(isIntakeStation), [stations]);
  const isParkingPartialSelection = parkingStations.length !== stations.length;
  // null shows 'Mixed'
  const canBeUsedForParking = useMemo(
    () =>
      getCommonBoolean(parkingStations.map(({ poiSpec }) => poiSpec.canBeUsedForParking ?? false)),
    [parkingStations],
  );

  // Priority only applies to stations that allow parking: shown when any does, editable when all do
  const priorityStations = useMemo(
    () => parkingStations.filter(hasParkingPriority),
    [parkingStations],
  );
  const isPriorityPartialSelection = priorityStations.length !== stations.length;
  // null shows 'Mixed'
  const priority = useMemo(
    () =>
      getCommonNumber(
        priorityStations.map(({ poiSpec }) => poiSpec.parkingExtension?.priority ?? 0),
      ),
    [priorityStations],
  );

  /** Applies the same POI spec change to every selected station in one update (one undo step). */
  const updatePoiSpecs = useCallback(
    (updateSpec: (spec: LoadHandlingPoiSpecType) => LoadHandlingPoiSpecType) => {
      const updatedAreas = createEmptyAreas();
      stations.forEach(({ area }) => {
        pushAreaToItsCollection(updatedAreas, {
          ...area,
          loadHandlingPoiSpecs: area.loadHandlingPoiSpecs.map(updateSpec),
        });
      });
      fddmSvc.updateAreas(updatedAreas);
    },
    [fddmSvc, stations],
  );

  const onDeltaXChange = useCallback(
    (newValueMm: number) => {
      if (Number.isNaN(newValueMm)) return;

      const newValue = NumUtils.mmToMeters(newValueMm);
      fddmSvc.setPreference('singularStationArea.loadHandlingPoiSpecs.deltaX', newValue);
      updatePoiSpecs((spec) => ({ ...spec, deltaX: newValue }));
    },
    [fddmSvc, updatePoiSpecs],
  );

  const onDeltaYChange = useCallback(
    (newValueMm: number) => {
      if (Number.isNaN(newValueMm)) return;

      const newValue = NumUtils.mmToMeters(newValueMm);
      fddmSvc.setPreference('singularStationArea.loadHandlingPoiSpecs.deltaY', newValue);
      updatePoiSpecs((spec) => ({ ...spec, deltaY: newValue }));
    },
    [fddmSvc, updatePoiSpecs],
  );

  const onParkingChange = useCallback(
    (_event: ChangeEvent<HTMLInputElement>, newValue: boolean) => {
      if (isParkingPartialSelection) return;

      const changedStations = parkingStations.filter(
        ({ poiSpec }) => (poiSpec.canBeUsedForParking ?? false) !== newValue,
      );
      if (changedStations.length === 0) return;

      fddmSvc.setPreference(
        'singularStationArea.loadHandlingPoiSpecs.canBeUsedForParking',
        newValue,
      );

      const parkingExtension = newValue
        ? {
            priority: fddmSvc.getPreference(
              'singularStationArea.loadHandlingPoiSpecs.parkingExtension.priority',
            ),
          }
        : undefined;

      const updatedAreas = createEmptyAreas();
      changedStations.forEach(({ area }) => {
        pushAreaToItsCollection(updatedAreas, {
          ...area,
          loadHandlingPoiSpecs: area.loadHandlingPoiSpecs.map((spec) => ({
            ...spec,
            canBeUsedForParking: newValue,
            parkingExtension,
          })),
        });
      });
      fddmSvc.updateAreas(updatedAreas);

      if (newValue) return;

      // reset preAnnounceDuration on related flows
      const { getRelatedFlows } = useFloorplanDesignStore.getState();
      const updatedFlows = getRelatedFlows(changedStations.map(({ area }) => area.id)).flatMap(
        (flow) => {
          const updatedFlow = withoutPreAnnounceDuration(flow);
          return updatedFlow ? [updatedFlow] : [];
        },
      );

      if (updatedFlows.length > 0) {
        snackbarSvc.showMessage(t('interface:properties.flows.pre_announcement.messages.reset'));
        fddmSvc.updateFlows(updatedFlows);
      }
    },
    [isParkingPartialSelection, parkingStations, fddmSvc, snackbarSvc, t],
  );

  const onParkingPriorityChange = useCallback(
    (newValue: number) => {
      if (isPriorityPartialSelection || Number.isNaN(newValue)) return;

      fddmSvc.setPreference(
        'singularStationArea.loadHandlingPoiSpecs.parkingExtension.priority',
        newValue,
      );
      updatePoiSpecs((spec) => ({ ...spec, parkingExtension: { priority: newValue } }));
    },
    [fddmSvc, isPriorityPartialSelection, updatePoiSpecs],
  );

  // Hidden when any selected station has no load handling POI
  if (stations.length === 0 || stations.length !== selectedSingularStationAreas.length) {
    return null;
  }

  return (
    <>
      <PropertiesLabel i18nkey='interface:properties.area.deltaX'>
        <NumberInput
          value={commonValues.deltaXMm}
          unit='mm'
          prefix={<GapHorizontalIcon />}
          sx={{ width: '100%' }}
          onChange={onDeltaXChange}
        />
      </PropertiesLabel>

      <PropertiesLabel i18nkey='interface:properties.area.deltaY'>
        <NumberInput
          value={commonValues.deltaYMm}
          unit='mm'
          prefix={<GapHorizontalIcon />}
          sx={{ width: '100%' }}
          onChange={onDeltaYChange}
        />
      </PropertiesLabel>
      {parkingStations.length > 0 && (
        <PropertiesLabel i18nkey='interface:properties.area.can_be_used_for_parking'>
          <DisabledReasonTooltip
            reasonKey={
              isParkingPartialSelection
                ? 'interface:properties.area.partial_selection.only_intake_stations'
                : undefined
            }
          >
            <MixedSwitch
              disabled={isDisabled || isParkingPartialSelection}
              checked={canBeUsedForParking}
              onChange={onParkingChange}
            />
          </DisabledReasonTooltip>
        </PropertiesLabel>
      )}
      {priorityStations.length > 0 && (
        <PropertiesLabel i18nkey='interface:properties.area.priority'>
          <DisabledReasonTooltip
            reasonKey={
              isPriorityPartialSelection
                ? 'interface:properties.area.partial_selection.only_parking_stations'
                : undefined
            }
          >
            <NumberInput
              // undefined (not false) keeps NumberInput's own read-only-mode check
              disabled={isPriorityPartialSelection ? true : undefined}
              value={priority}
              sx={{ width: '100%' }}
              minValue={0}
              onChange={onParkingPriorityChange}
            />
          </DisabledReasonTooltip>
        </PropertiesLabel>
      )}
    </>
  );
}

export const LoadHandlingPoiSpec = memo(LoadHandlingPoiSpecComponent);
